<template>
  <div class="absent-page">
    <!-- ============ 顶部：周期快捷切换 ============ -->
    <div class="hero">
      <div class="hero-left">
        <div class="hero-title">未出勤学员跟进</div>
        <div class="hero-sub">
          一键导出「一周 / 一个月 / 多个月未出勤」订单列表，数据仅限你管辖的校区，按跟进优先级排序
        </div>
      </div>
      <div class="period-tabs">
        <div
            v-for="p in periodOptions"
            :key="p.value"
            class="period-tab"
            :class="{ active: query.period === p.value }"
            @click="switchPeriod(p.value)">
          <div class="pt-name">{{ p.label }}</div>
          <div class="pt-desc">{{ p.desc }}</div>
        </div>
      </div>
    </div>

    <!-- ============ 筛选条件 ============ -->
    <div class="handle-box">
      <div class="filter-row">
        <select-area @areaListId="getAreaListId" :clear="'1'"/>
        <el-input
            v-model="query.keyword"
            clearable
            placeholder="学员姓名 / 手机号"
            style="width: 190px"
            class="mr10"
            @keyup.enter="search"></el-input>
        <el-input
            v-model="query.serveCoachName"
            clearable
            placeholder="服务老师"
            style="width: 150px"
            class="mr10"
            @keyup.enter="search"></el-input>
        <el-select
            v-model="query.manageWay"
            placeholder="校区类型"
            clearable
            style="width: 130px"
            class="mr10">
          <el-option label="直营" :value="1"/>
          <el-option label="加盟" :value="0"/>
          <el-option label="第三方" :value="2"/>
        </el-select>
        <el-input-number
            v-model="query.expiringDays"
            :min="0"
            :max="365"
            controls-position="right"
            style="width: 130px"
            class="mr10"></el-input-number>
        <el-button type="primary" icon="Search" @click="search">查询</el-button>
        <el-button icon="RefreshLeft" @click="reset">重置</el-button>
      </div>
      <div class="filter-tip">
        <el-icon><InfoFilled /></el-icon>
        <span>
          「临期天数」用于划分跟进优先级：卡在 {{ query.expiringDays }} 天内到期仍未到店 → P1 最紧急；
          已用课时 > 0 说明曾上课本期没来 → P2；已用课时 = 0 长期休眠 → P3；卡已过期 → R 状态待维护。
        </span>
      </div>
    </div>

    <!-- ============ 汇总看板（按校区） ============ -->
    <el-card shadow="never" class="summary-card">
      <template #header>
        <div class="card-head">
          <span class="card-title">{{ periodLabel }} · 按校区汇总</span>
          <el-popconfirm @confirm="exportSummary" title="确定要导出汇总表吗？">
            <template #reference>
              <el-button v-permission="['absentStudent:export']" size="small" type="primary" plain icon="Download">
                导出汇总
              </el-button>
            </template>
          </el-popconfirm>
        </div>
      </template>
      <el-table
          v-loading="summaryLoading"
          :data="summaryData"
          border
          size="small"
          header-cell-class-name="table-header"
          empty-text="暂无数据">
        <el-table-column prop="city" label="城市" min-width="90"/>
        <el-table-column prop="campusName" label="校区" min-width="180" show-overflow-tooltip/>
        <el-table-column prop="manageWayName" label="类型" width="80" align="center"/>
        <el-table-column prop="totalCount" label="在读卡数" width="100" align="center"/>
        <el-table-column prop="absentCount" label="未出勤卡数" width="110" align="center">
          <template #default="scope">
            <span class="num-danger">{{ scope.row.absentCount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="未出勤率" min-width="220">
          <template #default="scope">
            <el-progress
                :percentage="rateOf(scope.row)"
                :color="rateColor(scope.row)"
                :stroke-width="14"
                text-inside></el-progress>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="110" align="center" fixed="right">
          <template #default="scope">
            <el-button link type="primary" size="small" @click="drillCampus(scope.row)">查看学员</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- ============ 未出勤明细 ============ -->
    <el-card shadow="never">
      <template #header>
        <div class="card-head">
          <span class="card-title">
            {{ periodLabel }} · 未出勤学员明细
            <span v-if="tableTotal" class="card-count">共 {{ tableTotal }} 条</span>
          </span>
          <div class="card-actions">
            <el-select v-model="priorityFilter" placeholder="全部优先级" clearable size="small"
                       style="width: 165px" @change="search">
              <el-option label="P1-临期未到店" value="P1-临期未到店"/>
              <el-option label="P2-曾上课本期缺勤" value="P2-曾上课本期缺勤"/>
              <el-option label="P3-长期休眠" value="P3-长期休眠"/>
              <el-option label="R-已过期" value="R-已过期"/>
            </el-select>
            <el-popconfirm @confirm="exportExcel" title="确定要导出吗？导出后请到右上角下载中心查看">
              <template #reference>
                <el-button
                    v-permission="['absentStudent:export']"
                    size="small"
                    type="primary"
                    icon="Download"
                    :loading="exportLoading">一键导出</el-button>
              </template>
            </el-popconfirm>
          </div>
        </div>
      </template>

      <el-table
          v-loading="tableLoading"
          :data="tableData"
          border
          header-cell-class-name="table-header"
          empty-text="该周期内没有未出勤学员">
        <el-table-column label="跟进优先级" width="150" fixed="left">
          <template #default="scope">
            <el-tag :type="tagType(scope.row.priority)" effect="dark" size="small">
              {{ scope.row.priority }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="优先级说明" min-width="240" show-overflow-tooltip>
          <template #default="scope">
            <span class="remark-text">{{ scope.row.priorityRemark }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="city" label="城市" width="90"/>
        <el-table-column prop="campusName" label="校区" min-width="150" show-overflow-tooltip/>
        <el-table-column label="学员" min-width="120">
          <template #default="scope">
            <div>{{ scope.row.studentName }}</div>
            <div class="sub-text">{{ scope.row.studentPhone }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="orderId" label="订单ID" width="90"/>
        <el-table-column prop="cardName" label="课程类型" min-width="130" show-overflow-tooltip/>
        <el-table-column label="服务老师" width="100">
          <template #default="scope">{{ scope.row.serveCoachName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="remaining" label="剩余课时" width="95" align="center"/>
        <el-table-column prop="ouse" label="已用课时" width="95" align="center"/>
        <el-table-column prop="noCourseDays" label="未到店天数" width="110" align="center">
          <template #default="scope">
            <span class="num-danger">{{ scope.row.noCourseDays }} 天</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" label="卡到期日" width="115">
          <template #default="scope">
            <div>{{ fmtDate(scope.row.endTime) }}</div>
            <div class="sub-text">{{ scope.row.expireRemark }}</div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
            style="text-align: right;margin: 20px 10px;"
            :page-sizes="[20, 50, 100, 200]"
            :page-size="query.pageSize"
            :current-page="query.pageIndex"
            @current-change="selectPage"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableTotal">
        </el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import {
  absentPage,
  absentSummary,
  exportAbsent,
  exportAbsentSummary
} from "@/api/absentStudent";
import SelectArea from "../../components/SelectArea";

export default {
  name: "AbsentStudent",
  components: {
    SelectArea
  },
  data() {
    return {
      periodOptions: [
        { value: 1, label: "一周未出勤", desc: "近 7 天无签课" },
        { value: 2, label: "一个月未出勤", desc: "近 1 个月无签课" },
        { value: 3, label: "多个月未出勤", desc: "长期沉睡卡" }
      ],
      priorityFilter: "",
      tableLoading: false,
      summaryLoading: false,
      exportLoading: false,
      tableData: [],
      tableTotal: 0,
      summaryData: [],
      query: {
        areaIdList: [],
        period: 1,
        months: 3,
        expiringDays: 30,
        manageWay: null,
        keyword: "",
        serveCoachName: "",
        pageIndex: 1,
        pageSize: 20
      }
    };
  },
  computed: {
    periodLabel() {
      if (this.query.period === 3) {
        return (this.query.months || 3) + "个月未出勤";
      }
      return this.query.period === 2 ? "一个月未出勤" : "一周未出勤";
    },
    listQuery() {
      const q = { ...this.query };
      if (this.priorityFilter) {
        q.priority = this.priorityFilter;
      }
      return q;
    }
  },
  methods: {
    getAreaListId(val) {
      this.query.areaIdList = val && val.length !== undefined ? val : [];
      this.search();
    },
    switchPeriod(val) {
      if (this.query.period === val) return;
      this.query.period = val;
      this.query.pageIndex = 1;
      this.loadData();
    },
    search() {
      this.query.pageIndex = 1;
      this.loadData();
    },
    reset() {
      this.query.period = 1;
      this.query.months = 3;
      this.query.expiringDays = 30;
      this.query.manageWay = null;
      this.query.keyword = "";
      this.query.serveCoachName = "";
      this.priorityFilter = "";
      this.query.pageIndex = 1;
      this.loadData();
    },
    selectPage(index) {
      this.query.pageIndex = index;
      this.loadData();
    },
    drillCampus(row) {
      // 下钻：把校区写回筛选条件后重查明细
      this.query.areaIdList = [row.campusId];
      this.query.pageIndex = 1;
      this.loadData();
      this.$message.info(`已筛选「${row.campusName}」的未出勤学员`);
    },
    loadData() {
      this.tableLoading = true;
      absentPage(this.listQuery).then(val => {
        this.tableLoading = false;
        if (val.code === 200) {
          this.tableData = val.data.records || [];
          this.tableTotal = val.data.total || 0;
        } else {
          this.$message.error(val.message || "获取未出勤学员失败");
        }
      }).catch(() => {
        this.tableLoading = false;
      });
      this.loadSummary();
    },
    loadSummary() {
      this.summaryLoading = true;
      absentSummary(this.listQuery).then(val => {
        this.summaryLoading = false;
        if (val.code === 200) {
          this.summaryData = val.data || [];
        }
      }).catch(() => {
        this.summaryLoading = false;
      });
    },
    exportExcel() {
      this.exportLoading = true;
      exportAbsent(this.listQuery).then(val => {
        this.exportLoading = false;
        if (val.code === 200) {
          this.$message.success("导出成功，请到右上角下载中心查看！");
        } else {
          this.$message.error(val.message || "导出失败");
        }
      }).catch(() => {
        this.exportLoading = false;
      });
    },
    exportSummary() {
      exportAbsentSummary(this.listQuery).then(val => {
        if (val.code === 200) {
          this.$message.success("导出成功，请到右上角下载中心查看！");
        } else {
          this.$message.error(val.message || "导出失败");
        }
      });
    },
    rateOf(row) {
      const total = Number(row.totalCount || 0);
      const absent = Number(row.absentCount || 0);
      if (!total) return 0;
      return Math.min(100, Math.round((absent * 100) / total));
    },
    rateColor(row) {
      const r = this.rateOf(row);
      if (r >= 35) return "#f56c6c";
      if (r >= 20) return "#e6a23c";
      return "#67c23a";
    },
    tagType(priority) {
      if (!priority) return "info";
      if (priority.startsWith("P1")) return "danger";
      if (priority.startsWith("P2")) return "warning";
      if (priority.startsWith("P3")) return "info";
      return "";
    },
    fmtDate(val) {
      if (!val) return "-";
      return String(val).substring(0, 10);
    }
  },
  created() {
    this.loadData();
  }
};
</script>

<style scoped>
.absent-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100%;
}

/* ---------- 顶部周期切换 ---------- */
.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  margin-bottom: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6b4cff 0%, #8a5cf6 55%, #a86ef0 100%);
  color: #fff;
  box-shadow: 0 6px 18px rgba(107, 76, 255, 0.22);
}
.hero-title {
  font-size: 19px;
  font-weight: 600;
  letter-spacing: 1px;
}
.hero-sub {
  margin-top: 6px;
  font-size: 13px;
  opacity: 0.86;
}
.period-tabs {
  display: flex;
  gap: 10px;
}
.period-tab {
  min-width: 132px;
  padding: 10px 16px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  cursor: pointer;
  transition: all 0.22s ease;
  text-align: center;
}
.period-tab:hover {
  background: rgba(255, 255, 255, 0.26);
  transform: translateY(-2px);
}
.period-tab.active {
  background: #fff;
  color: #6b4cff;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.14);
}
.pt-name {
  font-size: 14px;
}
.pt-desc {
  font-size: 11px;
  opacity: 0.8;
  margin-top: 3px;
}

/* ---------- 筛选 ---------- */
.handle-box {
  background: #fff;
  padding: 16px 18px 10px;
  margin-bottom: 14px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.filter-tip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 10px;
  background: #f4f0ff;
  border-radius: 6px;
  color: #5b4a9e;
  font-size: 12px;
  line-height: 1.6;
}
.mr10 {
  margin-right: 10px;
}

/* ---------- 卡片 ---------- */
.summary-card,
.el-card {
  margin-bottom: 14px;
  border-radius: 8px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.card-count {
  margin-left: 8px;
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}

/* ---------- 表格细节 ---------- */
.num-danger {
  color: #f56c6c;
  font-weight: 600;
}
.remark-text {
  color: #606266;
  font-size: 12px;
}
.sub-text {
  font-size: 12px;
  color: #909399;
}
</style>